import React, { FunctionComponent, useState } from "react";
import SocialCard from "./SocialCard";
import { Box, Button } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import { SocialItem } from "../../service/data";
import { useSocialCardStackStyles } from "./useSocialCardStackStyles";
import { useTranslate } from "../../service/translation";
import classNames from "classnames";

interface SocialCardStackProps {
    /** A run of consecutive posts by one contributor, newest first. At least two. */
    items: SocialItem[];
}

/**
 * Several consecutive posts by one account, shown as the newest post with the rest folded behind it.
 *
 * Accounts that post in bursts otherwise fill the screen and push everyone else out of view. The
 * newest post is kept whole rather than summarised, so the feed still reads as a feed: the point is
 * to stop a burst dominating, not to hide what was said.
 *
 * The wrapper carries `masonryItem` and the cards inside do not (see SocialCard's `inStack`), because
 * on the timeline the stack is the thing masonry lays out. Its own children must be invisible to it.
 */
const SocialCardStack: FunctionComponent<SocialCardStackProps> = (props: SocialCardStackProps) => {
    const { items } = props;
    const classes = useSocialCardStackStyles();
    const translate = useTranslate();
    const [expanded, setExpanded] = useState(false);

    const [newest, ...rest] = items;
    // Institutional contributors have empty firstName/lastName, so the account's display name is the
    // only one available for them. Without this the label read "1 more from" with nothing after it.
    const name = `${newest.mep.firstName} ${newest.mep.lastName}`.trim() || (newest.accountName ?? "");

    return (
        <Box className={classNames(classes.root, "masonryItem")}>
            <SocialCard record={newest} inStack />

            {expanded && rest.map(record => (
                <SocialCard key={record.id} record={record} inStack />
            ))}

            <Button
                className={classes.toggle}
                size="small"
                fullWidth
                onClick={() => setExpanded(! expanded)}
                startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            >
                {expanded
                    ? translate(name ? "social.message.collapsePosts" : "social.message.collapsePostsPlain", { name })
                    : translate(name ? "social.message.morePostsFrom" : "social.message.morePosts",
                        { count: rest.length, name })}
            </Button>
        </Box>
    );
};

export default SocialCardStack;
